/**
 * Read switch for a dai's config on a given day.
 *
 * A day's DAIS entry can carry its own frozen copy of the master's config (a
 * "snapshot", written by phase 3+). Once that exists, the day must keep showing it
 * even after the master is edited afterwards - that is the whole point of the
 * feature. This helper is the single place that decides, per entry, whether to read
 * the entry's own snapshot or fall back to the live master:
 *
 *   - SCHEMAVER === 2 (on the owning BathDaily) AND the entry's MASTERUPDDATE is set
 *     -> read the entry's own fields.
 *   - else if the live master still has this dai -> read the master, as before this
 *     feature existed.
 *   - else -> orphan: the dai is gone from every master's BATHDAIS and this entry has
 *     no snapshot to fall back on. Render placeholders; never throw.
 *
 * Every day-scoped read site (a site that renders or evaluates an EXISTING per-day
 * DAIS entry) must route through this function instead of reading `master.BATHDAIS`
 * directly. Sites that seed a brand-new entry (a dai not yet on the day, e.g. dragged
 * in from the sidebar) have no entry to check and correctly keep reading the master -
 * that is the source a snapshot is first taken from.
 */
import { BathDais, DAIS } from '@core/models/models/bathe.model';

export type DaiConfigSource = 'snapshot' | 'master' | 'orphan';

export interface ResolvedDaiConfig {
    bathMethodCd: number | null;
    bathWeeks: string | null;
    bathDays: string | null;
    priorityCd: number | null;
    treatmentNotes: string | null;
    blockCd: string | null;
    unitCd: string | null;
    bathName: string | null;
    source: DaiConfigSource;
}

/**
 * Resolve the effective config for one DAIS entry on one day.
 * @param entry The day's own DAIS entry for this dai. Undefined when the entry does not exist yet.
 * @param schemaVer The owning BathDaily's SCHEMAVER.
 * @param masterDai The dai's current entry in its owning BathMaster's BATHDAIS, if any.
 * @param masterBlockCd BLOCKCD of the room the master dai belongs to (BLOCKCD/UNITCD/BATHNAME live on the room, not the dai).
 * @param masterUnitCd UNITCD of the room the master dai belongs to.
 * @param masterBathName BATHNAME of the room the master dai belongs to.
 */
export function resolveDaiConfig(
    entry: DAIS | undefined,
    schemaVer: number | null | undefined,
    masterDai: BathDais | undefined,
    masterBlockCd?: string | null,
    masterUnitCd?: string | null,
    masterBathName?: string | null
): ResolvedDaiConfig {
    if (entry && schemaVer === 2 && !!entry.MASTERUPDDATE) {
        return {
            bathMethodCd: entry.BATHMETHODCD ?? null,
            bathWeeks: entry.BATHWEEKS ?? null,
            bathDays: entry.BATHDAYS ?? null,
            priorityCd: entry.PRIORITYCD ?? null,
            treatmentNotes: entry.TREATMENTNOTES ?? null,
            blockCd: entry.BLOCKCD ?? masterBlockCd ?? null,
            unitCd: entry.UNITCD ?? masterUnitCd ?? null,
            bathName: entry.BATHNAME ?? masterBathName ?? null,
            source: 'snapshot',
        };
    }

    if (masterDai) {
        return {
            bathMethodCd: masterDai.BATHMETHODCD ?? null,
            bathWeeks: masterDai.BATHWEEKS ?? null,
            bathDays: masterDai.BATHDAYS ?? null,
            priorityCd: masterDai.PRIORITYCD ?? null,
            treatmentNotes: masterDai.TREATMENTNOTES ?? null,
            blockCd: masterBlockCd ?? null,
            unitCd: masterUnitCd ?? null,
            bathName: masterBathName ?? null,
            source: 'master',
        };
    }

    return {
        bathMethodCd: null,
        bathWeeks: null,
        bathDays: null,
        priorityCd: null,
        treatmentNotes: null,
        blockCd: null,
        unitCd: null,
        bathName: null,
        source: 'orphan',
    };
}
